import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import satService, { SAT_KIND_LABELS, SAT_MOOD_LABELS, SatStudent } from '../../services/sat.service';

const kips = (n: number) => `${n.toLocaleString('fr-FR')} ₭`;
const num = { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const;

/** Onglet « Sat » de la page Élèves : solde, barème, capacité, ajustements, journal et estimations de maîtrise. */
export default function StudentSatPanel({ studentId }: { studentId: number }) {
  const [data, setData] = useState<SatStudent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [override, setOverride] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await satService.getStudent(studentId);
      setData(d);
      setOverride(d.capacity.capacity_override == null ? '' : String(d.capacity.capacity_override));
      setError(null);
    } catch {
      setError('Impossible de charger le portefeuille Sat de cet élève.');
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const saveCapacity = async () => {
    setBusy(true);
    try {
      setData(await satService.setCapacity(studentId, override.trim() === '' ? null : Number(override.replace(',', '.'))));
      toast.success('Capacité enregistrée');
    } catch {
      toast.error('Capacité invalide');
    } finally {
      setBusy(false);
    }
  };

  const adjust = async () => {
    const n = Number(amount);
    if (!Number.isInteger(n) || n === 0) { toast.error('Montant entier non nul attendu'); return; }
    setBusy(true);
    try {
      setData(await satService.adjust(studentId, n, note));
      setAmount('');
      setNote('');
      toast.success(n > 0 ? `${kips(n)} crédités` : `${kips(-n)} débités`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Ajustement refusé');
    } finally {
      setBusy(false);
    }
  };

  const revive = async (petId: number, name: string) => {
    if (!window.confirm(`Ressusciter ${name} ? Action exceptionnelle (bug de barème), journalisée.`)) return;
    setBusy(true);
    try {
      setData(await satService.revive(studentId, petId));
      toast.success(`${name} est de retour`);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Résurrection refusée');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>;
  const t = data.tariff;

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline">Solde</Typography>
            <Typography variant="h4">{kips(data.balance)}</Typography>
            <Typography variant="body2" color="text.secondary">
              Quiz aujourd'hui : {t.quiz_today} / {t.quiz_daily_cap}
              {t.streak_bonus > 0 && ` · série en cours (+${Math.round(t.streak_bonus * 100)} %)`}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="overline">Barème actuel</Typography>
            <Typography variant="body2">
              Un exercice parfait sur un savoir-faire inconnu rapporte jusqu'à <b>{kips(t.base_price)}</b>
              {' '}(S = {kips(t.S)} par semaine de travail attendu) ; une question de quiz jusqu'à <b>{kips(t.Bq)}</b>.
              Plus le savoir-faire est estimé maîtrisé, moins il rapporte (jusqu'à 4 fois moins).
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                Quota {data.capacity.quota.toFixed(1)} · capacité estimée {data.capacity.capacity.toFixed(1)} ex./sem.
              </Typography>
              <TextField
                size="small"
                label="Capacité forcée"
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                placeholder="vide = auto"
                sx={{ width: 150 }}
              />
              <Button size="small" variant="outlined" onClick={saveCapacity} disabled={busy}>Enregistrer</Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline">Animal</Typography>
            {data.pet ? (
              <>
                <Typography variant="h6">
                  {data.pet.name} <Typography component="span" color="text.secondary">({data.pet.species_label})</Typography>
                </Typography>
                <Typography variant="body2">
                  {data.pet.stage} · {data.pet.age_weeks} sem · {SAT_MOOD_LABELS[data.pet.mood]} · logement : {data.pet.housing_key}
                </Typography>
                <Typography variant="body2" color={data.pet.hunger_days > 0 || data.pet.homeless_days > 0 ? 'error.main' : 'text.secondary'}>
                  {data.pet.food_rations} ration(s) · {data.pet.hunger_days} jour(s) sans manger · {data.pet.homeless_days} jour(s) dehors
                </Typography>
              </>
            ) : (
              <Typography color="text.secondary">Pas d'animal vivant : l'élève doit en adopter un dans son onglet Sat.</Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline">Cimetière</Typography>
            {data.cemetery.length === 0 && <Typography color="text.secondary">Aucun animal mort.</Typography>}
            {data.cemetery.map((g) => (
              <Stack key={g.id} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  <b>{g.name}</b> ({g.species_label}), {g.death_cause === 'hunger' ? 'mort de faim' : 'mort dans la rue'} le{' '}
                  {new Date(g.died_at).toLocaleDateString('fr-FR')} · {kips(g.balance)} laissés
                </Typography>
                {!data.pet && (
                  <Button size="small" variant="outlined" color="warning" disabled={busy} onClick={() => revive(g.id, g.name)}>
                    Ressusciter
                  </Button>
                )}
              </Stack>
            ))}
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="overline">Crédit / débit manuel</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
          <TextField size="small" label="Montant (₭, négatif pour débiter)" value={amount}
            onChange={(e) => setAmount(e.target.value)} sx={{ width: 240 }} />
          <TextField size="small" label="Note" value={note} onChange={(e) => setNote(e.target.value)} fullWidth />
          <Button variant="contained" onClick={adjust} disabled={busy}>Appliquer</Button>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Journal</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell>Note</TableCell>
                <TableCell sx={num}>Montant</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.ledger.length === 0 && (
                <TableRow><TableCell colSpan={4}><Typography color="text.secondary">Aucune transaction.</Typography></TableCell></TableRow>
              )}
              {data.ledger.map((tr) => (
                <TableRow key={tr.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(tr.created_at).toLocaleString('fr-FR')}</TableCell>
                  <TableCell>{SAT_KIND_LABELS[tr.kind] ?? tr.kind}</TableCell>
                  <TableCell>{tr.note}</TableCell>
                  <TableCell sx={{ ...num, color: tr.amount >= 0 ? 'success.main' : 'error.main' }}>
                    {tr.amount > 0 ? '+' : ''}{tr.amount}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Savoir-faire les moins maîtrisés (estimation)</Typography>
          <Table size="small">
            <TableHead>
              <TableRow><TableCell>Savoir-faire</TableCell><TableCell sx={num}>p</TableCell><TableCell sx={num}>obs.</TableCell></TableRow>
            </TableHead>
            <TableBody>
              {data.estimates.length === 0 && (
                <TableRow><TableCell colSpan={3}><Typography color="text.secondary">Aucune observation pour l'instant.</Typography></TableCell></TableRow>
              )}
              {data.estimates.map((e) => (
                <TableRow key={e.skill_id}>
                  <TableCell>{e.skill_name}</TableCell>
                  <TableCell sx={num}>{Math.round(e.p * 100)} %</TableCell>
                  <TableCell sx={num}>{e.n_obs}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Grid>
      </Grid>
    </Stack>
  );
}
